import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, X } from "lucide-react";
import { useApi } from "../utils/Api";
import SEO from "../components/SEO";
import PageHero from "../components/PageHero";
import PageIntro from "../components/PageIntro";
import heroShop from "../assets/heroes/hero-shop.webp";
import CtaBand from "../components/CtaBand";
import ctaPrints from "../assets/heroes/hero-prints.webp";
import { PAGES } from "../seoContent";

function getPrimaryImage(product) {
  const image = product.image_url;

  if (!image) {
    return "/placeholder-product.jpg";
  }

  if (image.startsWith("http://") || image.startsWith("https://")) {
    return image;
  }

  return `https://kinewsstudiolimited.co.uk/${image.replace(/^\/+/, "")}`;
}

export default function Shop() {
  const { apiFetch } = useApi();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");

  useEffect(() => {
    let mounted = true;

    const loadShopData = async () => {
      try {
        setLoading(true);
        setLoadError("");

        const [productsRes, categoriesRes] = await Promise.all([
          apiFetch("list_public_shop_products"),
          apiFetch("list_public_shop_categories"),
        ]);

        if (!productsRes?.success) {
          throw new Error(productsRes?.message || "Failed to load products.");
        }

        if (!categoriesRes?.success) {
          throw new Error(
            categoriesRes?.message || "Failed to load categories.",
          );
        }

        if (!mounted) return;

        setProducts(
          Array.isArray(productsRes.data)
            ? productsRes.data.filter((item) => Number(item.is_active) === 1)
            : [],
        );

        setCategories(
          Array.isArray(categoriesRes.data)
            ? categoriesRes.data.filter((item) => Number(item.is_active) === 1)
            : [],
        );
      } catch (err) {
        if (!mounted) return;
        setLoadError(err.message || "Unable to load shop data.");
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadShopData();

    return () => {
      mounted = false;
    };
  }, [apiFetch]);

  const categoryTabs = useMemo(() => {
    return [
      { id: "All", name: "All" },
      ...categories.map((category) => ({
        id: String(category.id),
        name: category.name,
      })),
    ];
  }, [categories]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();

    return products.filter((product) => {
      const matchSearch =
        !s ||
        product.name?.toLowerCase().includes(s) ||
        (product.details || "").toLowerCase().includes(s);

      const matchCategory =
        activeCategory === "All" ||
        String(product.category_id) === String(activeCategory);

      return matchSearch && matchCategory;
    });
  }, [products, search, activeCategory]);

  return (
    <>
      <SEO
        title={PAGES["/shop"].title}
        description={PAGES["/shop"].description}
      />
      <main className="min-h-screen bg-white text-black">
        <PageHero
          title="Shop"
          image={heroShop}
          imagePosition="center 40%"
        />

        <PageIntro>
          Browse our custom frames, photo prints, posters, canvas and
          personalised gifts. Choose a product, customise and order directly
          online.
        </PageIntro>

        <section className="bg-[#fff7fb]">
          <div className="mx-auto max-w-7xl px-6 pb-16 pt-8 sm:px-10 lg:px-16 xl:px-10">
            <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
              <div className="flex flex-wrap items-center gap-3">
                {categoryTabs.map((tab) => {
                  const isActive = activeCategory === tab.id;

                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActiveCategory(tab.id)}
                      className={[
                        "rounded-2xl border px-5 py-2.5 text-sm font-semibold transition",
                        isActive
                          ? "border-pink-500 bg-pink-500 text-white shadow-md"
                          : "border-black/10 bg-white text-black/80 hover:border-pink-300 hover:bg-pink-50",
                      ].join(" ")}
                    >
                      {tab.name}
                    </button>
                  );
                })}
              </div>

              <div className="relative w-full md:w-[320px]">
                <Search
                  aria-hidden="true"
                  className="pointer-events-none absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-pink-500"
                />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search frames, prints, gifts..."
                  aria-label="Search products"
                  className="w-full rounded-full border border-black/10 bg-white py-3 pl-11 pr-11 text-base text-black shadow-[0_8px_24px_rgba(0,0,0,0.05)] outline-none transition placeholder:text-black/40 focus:border-pink-500 focus:shadow-[0_8px_24px_rgba(236,72,153,0.18)] focus:ring-4 focus:ring-pink-500/10"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    aria-label="Clear search"
                    className="absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-black/50 transition hover:bg-pink-50 hover:text-pink-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {loading ? (
              <div className="mt-10 rounded-3xl border border-black/10 bg-white p-8 shadow-[0_16px_40px_rgba(0,0,0,0.06)]">
                <p className="font-semibold">Loading products...</p>
              </div>
            ) : loadError ? (
              <div className="mt-10 rounded-3xl border border-red-200 bg-red-50 p-8">
                <p className="font-semibold text-red-700">{loadError}</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="mt-10 rounded-3xl border border-black/10 bg-white p-8 shadow-[0_16px_40px_rgba(0,0,0,0.06)]">
                <p className="font-semibold">No products found.</p>
                <p className="mt-2 text-sm text-black/60">
                  Try another category or keyword.
                </p>
              </div>
            ) : (
              <>
                <p className="mt-8 text-sm text-black/50">
                  {filtered.length} product{filtered.length !== 1 ? "s" : ""}
                </p>

                <div className="mt-4 grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {filtered.map((product) => (
                    <Link
                      key={product.id}
                      to={`/shop/${product.id}`}
                      className="group flex flex-col overflow-hidden rounded-3xl border border-black/10 bg-white shadow-[0_16px_40px_rgba(0,0,0,0.06)] transition duration-300 hover:-translate-y-1 hover:border-pink-300 hover:shadow-[0_22px_50px_rgba(0,0,0,0.12)]"
                    >
                      <div className="relative aspect-[4/3] overflow-hidden bg-black/[0.04]">
                        <img
                          src={getPrimaryImage(product)}
                          alt={product.name}
                          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>

                      <div className="flex flex-1 flex-col p-5">
                        <h3 className="text-base font-semibold text-black">
                          {product.name}
                        </h3>

                        <p className="mt-3 flex items-baseline gap-2">
                          <span className="text-xs uppercase tracking-wide text-black/45">
                            From
                          </span>
                          <span className="text-lg font-bold tracking-tight text-pink-600">
                            {product.base_price !== null
                              ? `£${Number(product.base_price).toFixed(2)}`
                              : "£0.00"}
                          </span>
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </>
            )}
          </div>
        </section>

        <section className="bg-white">
          <div className="mx-auto max-w-7xl px-6 py-14 sm:px-10 lg:px-16 xl:px-10">
            <CtaBand
              heading="Business Card, Flyer & Poster Printing"
              text="Order professional printing online from our Rochdale studio in just a few steps."
              to="/printing"
              label="Order Prints"
              image={ctaPrints}
            />
          </div>
        </section>
      </main>
    </>
  );
}
